import { clsx } from 'clsx'
import { type EntrySys } from 'contentful'
import { type HTMLAttributes, useCallback, useContext, useRef } from 'react'

import { useFirstEnterViewport } from '@lib/hooks/viewport-tracker'

import type { TypeAlignmentEntry } from '@contentful-types/alignment'
import type { Background } from '@contentful-types/background'
import type { TypeComponentTitleDescriptionBlockEntry } from '@contentful-types/component-title-description-block'

import { PageContext } from '@contexts/page-context'

import { SectionHeader } from '@components/section-header/section-header'
import { Section } from '@components/ui-core/section/section'
// import { gnarSpec } from '../../lib/tracking/gnar'

import { getBackgroundAndColorScheme } from './color-scheme'

import styles from './page-section.module.scss'

export const getSectionAnchorId = (sysStructure?: EntrySys): string | undefined => {
  const id = sysStructure?.id
  const contentTypeId = sysStructure?.contentType?.sys?.id

  return id && contentTypeId ? `${contentTypeId}_${id}` : undefined
}

const DEFAULT_FIRST_ENTER_VIEWPORT_THRESHOLD = 0.5

// TODO - Move this to a share place once section group is worked on
export interface SectionStructure {
  sysStructure: EntrySys
  background?: Background // this one will need to change or get removed - given the new semantic colors
  heading?: TypeComponentTitleDescriptionBlockEntry
}

export interface PageSectionProps extends SectionStructure, HTMLAttributes<HTMLElement> {
  internalName: string
  alignment?: TypeAlignmentEntry
  overrideOnFirstEnterViewport?: () => void
  overrideFirstEnterViewportThreshold?: number
  backgroundAssetType?: string
}

export const PageSection = ({
  internalName,
  sysStructure,
  className,
  children,
  background,
  heading,
  alignment,
  // overrideOnFirstEnterViewport,
  // overrideFirstEnterViewportThreshold,
  // backgroundAssetType,
}: PageSectionProps) => {
  const sectionContentContainerRef = useRef<HTMLDivElement>(null)
  const anchorId = getSectionAnchorId(sysStructure)
  const { pageId, slug, isSectionScrollTrackingEnabled } = useContext(PageContext)

  const onFirstEnter = useCallback(() => {
    if (isSectionScrollTrackingEnabled) {
      console.log(`section scrolled into view: ${internalName} ${pageId} ${slug}`)
      // gnarSpec.sectionScroll(internalName, pageId, slug)
    }
  }, [internalName, isSectionScrollTrackingEnabled, pageId, slug])

  const { colorScheme, background: semanticBackground } = getBackgroundAndColorScheme(background)

  useFirstEnterViewport(
    sectionContentContainerRef,
    onFirstEnter,
    DEFAULT_FIRST_ENTER_VIEWPORT_THRESHOLD,
  )

  return (
    <Section
      ref={sectionContentContainerRef}
      id={anchorId}
      data-component-id={internalName}
      data-color-scheme={colorScheme}
      data-background-color={semanticBackground}
      className={clsx(styles.pageSection, className)}
    >
      <SectionHeader heading={heading} alignment={alignment} />

      {children}
    </Section>
  )
}
